import React, { useRef } from 'react';
import { products } from '../data/mockData';
import { Phone, Mail, Globe, MapPin, Download } from 'lucide-react';
import html2pdf from 'html2pdf.js';

const Brochure = () => {
  const brochureRef = useRef();

  const handleDownloadPDF = () => {
    const element = brochureRef.current;
    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: 'ARK-Scaffolding-Product-Catalog.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      },
      jsPDF: { 
        unit: 'in', 
        format: 'a4', 
        orientation: 'portrait'
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    html2pdf().set(opt).from(element).save();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Download PDF Button */}
      <div className="no-print fixed top-4 right-4 z-50">
        <button
          onClick={handleDownloadPDF}
          className="flex items-center gap-2 bg-[#DAA520] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#B8860B] transition-colors duration-300 shadow-lg"
        >
          <Download size={20} />
          Download PDF
        </button>
      </div>

      {/* Brochure Content */}
      <div ref={brochureRef} className="brochure-content max-w-7xl mx-auto p-8">
        {/* Cover Page */}
        <div className="text-center mb-16 page-break-after">
          <div className="border-8 border-[#008080] rounded-lg p-12 bg-gradient-to-br from-gray-50 to-white">
            <img
              src="https://customer-assets.emergentagent.com/job_8a6bf79d-629c-4621-9de5-4520ae741e2c/artifacts/15sezi0u_f665ed4a-3e98-483b-93e6-39a122c5327d.jpeg"
              alt="ARK Scaffolding"
              className="h-40 mx-auto mb-6"
            />
            <h1 className="text-6xl font-bold text-[#008080] mb-4">ARK Scaffolding</h1>
            <p className="text-3xl text-[#DAA520] font-semibold mb-6">Product Catalog</p>
            <p className="text-2xl text-gray-600 italic mb-8">"Stable Solutions for Every Height"</p>
            <div className="w-32 h-1 bg-[#DAA520] mx-auto mb-8"></div>
            <p className="text-xl text-gray-700 font-semibold">Scaffolding Material on Hire</p>
            <p className="text-lg text-gray-600 mt-4">Residential | Commercial | Industrial</p>
          </div>
        </div>

        {/* About Us Section */}
        <div className="mb-16 page-break-before">
          <div className="bg-[#008080] text-white p-8 rounded-lg mb-8">
            <h2 className="text-4xl font-bold mb-4 text-center">About ARK Scaffolding</h2>
            <div className="w-24 h-1 bg-[#DAA520] mx-auto mb-6"></div>
          </div>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p className="text-lg">
              ARK Scaffolding is a trusted provider of high-quality scaffolding materials on hire, serving the construction industry with dedication and professionalism. We specialize in supplying robust, well-maintained scaffolding equipment for residential, commercial, and industrial projects across Pune and surrounding regions.
            </p>
            <p className="text-lg">
              Our commitment to safety, quality, and reliability has made us a preferred choice for contractors, builders, and construction companies. Every piece of equipment in our inventory undergoes rigorous quality checks and maintenance to ensure it meets the highest safety standards.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-[#DAA520]">
                <h3 className="text-xl font-bold text-[#008080] mb-3">Our Mission</h3>
                <p className="text-sm">To provide safe, reliable, and cost-effective scaffolding solutions that support successful project completion.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-[#DAA520]">
                <h3 className="text-xl font-bold text-[#008080] mb-3">Quality Assurance</h3>
                <p className="text-sm">All equipment is regularly inspected, maintained, and tested to meet industry safety standards.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-[#DAA520]">
                <h3 className="text-xl font-bold text-[#008080] mb-3">Service Excellence</h3>
                <p className="text-sm">Timely delivery, professional support, and flexible rental terms to suit your project needs.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information - Prominent */}
        <div className="mb-16 bg-gradient-to-r from-[#008080] to-[#006666] text-white p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#DAA520]">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start gap-4 bg-white/10 p-4 rounded-lg backdrop-blur">
                <Phone className="w-6 h-6 text-[#DAA520] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-lg">Phone & WhatsApp Business</p>
                  <p className="text-2xl font-bold text-[#DAA520]">+91 9766601155</p>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-white/10 p-4 rounded-lg backdrop-blur">
                <Mail className="w-6 h-6 text-[#DAA520] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-lg">Email</p>
                  <p className="text-xl">info@arkscaffolding.in</p>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-white/10 p-4 rounded-lg backdrop-blur">
                <Globe className="w-6 h-6 text-[#DAA520] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-lg">Website</p>
                  <p className="text-xl">www.arkscaffolding.in</p>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-start gap-4 bg-white/10 p-6 rounded-lg backdrop-blur h-full">
                <MapPin className="w-6 h-6 text-[#DAA520] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-lg mb-2">Our Location</p>
                  <p className="text-lg leading-relaxed">
                    Survey Number: 148/5<br/>
                    Opp. Sandeep Kate Bunglow<br/>
                    Pimple Saudagar<br/>
                    Pune - 411027, Maharashtra, India
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className="mb-16 page-break-before">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-[#008080] mb-4">Our Product Range</h2>
            <div className="w-32 h-1 bg-[#DAA520] mx-auto mb-4"></div>
            <p className="text-xl text-gray-600">Comprehensive Scaffolding Solutions for Every Construction Need</p>
          </div>
          
          <div className="space-y-8">
            {products.map((product, index) => (
              <div key={product.id} className={`page-break-inside-avoid ${index > 0 && index % 3 === 0 ? 'page-break-before' : ''}`}>
                <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-[#008080] transition-colors duration-300 bg-white shadow-md">
                  <div className="grid md:grid-cols-3 gap-6">
                    {/* Product Image */}
                    <div className="md:col-span-1">
                      <div className="relative h-56 bg-white flex items-center justify-center rounded border border-gray-200 p-4">
                        {/* Small Corner Logo - Reduced Size */}
                        <div className="absolute top-1 left-1 z-20 bg-white/95 p-1 rounded shadow-sm">
                          <img
                            src="https://customer-assets.emergentagent.com/job_8a6bf79d-629c-4621-9de5-4520ae741e2c/artifacts/15sezi0u_f665ed4a-3e98-483b-93e6-39a122c5327d.jpeg"
                            alt="ARK"
                            className="w-7 h-7 object-contain"
                          />
                        </div>
                        
                        <img
                          src={product.image}
                          alt={product.name}
                          className="relative h-full w-auto object-contain z-10"
                        />
                      </div>
                      <div className="mt-3 text-center">
                        <span className="inline-block bg-[#008080] text-white px-4 py-1 rounded-full text-sm font-semibold">
                          Item #{product.id}
                        </span>
                      </div>
                    </div>

                    {/* Product Details */}
                    <div className="md:col-span-2">
                      <h3 className="text-3xl font-bold text-[#008080] mb-3 border-b-3 border-[#DAA520] pb-2">
                        {product.name}
                      </h3>
                      
                      <p className="text-gray-700 mb-4 leading-relaxed text-base font-medium">
                        {product.description}
                      </p>
                      
                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {product.detailedDescription}
                        </p>
                      </div>

                      {/* Specifications */}
                      {product.specifications && (
                        <div className="bg-white border-l-4 border-[#DAA520] p-4 rounded">
                          <p className="font-bold text-[#008080] mb-3 text-sm uppercase tracking-wide">Key Specifications:</p>
                          <div className="grid grid-cols-2 gap-2">
                            {product.specifications.map((spec, index) => (
                              <div key={index} className="flex items-start gap-2">
                                <span className="text-[#DAA520] text-lg">✓</span>
                                <span className="text-sm text-gray-700">{spec}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cuplock System Section */}
        <div className="page-break-before mb-16">
          <div className="bg-[#008080] text-white p-8 rounded-lg mb-8">
            <h2 className="text-4xl font-bold mb-4 text-center">Cuplock Scaffolding System</h2>
            <div className="w-32 h-1 bg-[#DAA520] mx-auto mb-4"></div>
            <p className="text-center text-lg">Advanced Modular Scaffolding for Complex Projects</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <div className="mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_construction-hire/artifacts/o9axj8wg_6aa04f58-95f3-4283-a027-9c4170ecc676.jpeg"
                  alt="Cuplock System"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-[#008080] mb-4">System Overview</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                The Cuplock Scaffolding System is a modular scaffolding system known for its fast locking mechanism, 
                high load-bearing capacity, and safety. It uses a cup-and-blade locking arrangement for quick and 
                secure connections, making it ideal for buildings, bridges, industrial plants, and infrastructure projects.
              </p>
              
              <h3 className="text-2xl font-bold text-[#008080] mb-4">Applications</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-[#DAA520] font-bold">→</span>
                  <span>Residential and commercial building construction</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-[#DAA520] font-bold">→</span>
                  <span>Bridge construction and infrastructure projects</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-[#DAA520] font-bold">→</span>
                  <span>Industrial plants and refineries</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-[#DAA520] font-bold">→</span>
                  <span>Complex architectural structures</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white border-2 border-[#008080] p-6 rounded-lg">
              <h3 className="text-2xl font-bold mb-4 text-[#008080] border-b-2 border-[#DAA520] pb-2">Key Advantages</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-[#DAA520] text-2xl">✓</span>
                  <div>
                    <p className="font-semibold text-gray-800">Fast locking system</p>
                    <p className="text-sm text-gray-600">Quick assembly and disassembly</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#DAA520] text-2xl">✓</span>
                  <div>
                    <p className="font-semibold text-gray-800">High load-bearing capacity</p>
                    <p className="text-sm text-gray-600">Suitable for heavy-duty applications</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#DAA520] text-2xl">✓</span>
                  <div>
                    <p className="font-semibold text-gray-800">Suitable for complex structures</p>
                    <p className="text-sm text-gray-600">Flexible configuration options</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#DAA520] text-2xl">✓</span>
                  <div>
                    <p className="font-semibold text-gray-800">Minimal loose components</p>
                    <p className="text-sm text-gray-600">Reduced risk of lost parts</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#DAA520] text-2xl">✓</span>
                  <div>
                    <p className="font-semibold text-gray-800">Strong and durable</p>
                    <p className="text-sm text-gray-600">Long service life with proper maintenance</p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="bg-white border-2 border-[#DAA520] p-6 rounded-lg">
              <h3 className="text-2xl font-bold mb-4 text-[#008080] border-b-2 border-[#DAA520] pb-2">Main Components</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-[#008080] font-bold text-lg">1.</span>
                  <div>
                    <p className="font-semibold text-gray-800">Vertical Standards with cups</p>
                    <p className="text-sm text-gray-600">Main vertical load-bearing members</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#008080] font-bold text-lg">2.</span>
                  <div>
                    <p className="font-semibold text-gray-800">Ledgers</p>
                    <p className="text-sm text-gray-600">Horizontal connecting elements</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#008080] font-bold text-lg">3.</span>
                  <div>
                    <p className="font-semibold text-gray-800">Bracings</p>
                    <p className="text-sm text-gray-600">Diagonal stability components</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#008080] font-bold text-lg">4.</span>
                  <div>
                    <p className="font-semibold text-gray-800">Base Jacks</p>
                    <p className="text-sm text-gray-600">Ground-level height adjustment</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#008080] font-bold text-lg">5.</span>
                  <div>
                    <p className="font-semibold text-gray-800">U Jacks</p>
                    <p className="text-sm text-gray-600">Top-level support and adjustment</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Why Choose ARK Section */}
        <div className="mb-16 page-break-before">
          <div className="bg-[#008080] text-white p-8 rounded-lg mb-8 text-center">
            <h2 className="text-4xl font-bold mb-4">Why Choose ARK Scaffolding?</h2>
            <div className="w-32 h-1 bg-[#DAA520] mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border-2 border-[#008080] p-6 rounded-lg text-center hover:shadow-lg transition-shadow">
              <div className="w-20 h-20 bg-[#DAA520] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl text-white">★</span>
              </div>
              <h3 className="text-xl font-bold text-[#008080] mb-3">Quality Equipment</h3>
              <p className="text-gray-700 text-sm">High-grade, well-maintained scaffolding that meets all safety standards</p>
            </div>
            <div className="bg-white border-2 border-[#008080] p-6 rounded-lg text-center hover:shadow-lg transition-shadow">
              <div className="w-20 h-20 bg-[#DAA520] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl text-white">⚡</span>
              </div>
              <h3 className="text-xl font-bold text-[#008080] mb-3">Timely Service</h3>
              <p className="text-gray-700 text-sm">Prompt delivery and pickup to keep your project on schedule</p>
            </div>
            <div className="bg-white border-2 border-[#008080] p-6 rounded-lg text-center hover:shadow-lg transition-shadow">
              <div className="w-20 h-20 bg-[#DAA520] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl text-white">💰</span>
              </div>
              <h3 className="text-xl font-bold text-[#008080] mb-3">Competitive Pricing</h3>
              <p className="text-gray-700 text-sm">Cost-effective rental solutions without compromising quality</p>
            </div>
          </div>
        </div>

        {/* Footer / Back Cover */}
        <div className="text-center border-t-4 border-[#008080] pt-12 page-break-before">
          <img
            src="https://customer-assets.emergentagent.com/job_8a6bf79d-629c-4621-9de5-4520ae741e2c/artifacts/15sezi0u_f665ed4a-3e98-483b-93e6-39a122c5327d.jpeg"
            alt="ARK Scaffolding"
            className="h-24 mx-auto mb-6"
          />
          <h3 className="text-3xl font-bold text-[#008080] mb-4">
            For Quality Scaffolding Solutions
          </h3>
          <p className="text-xl text-gray-700 mb-6">Contact ARK Scaffolding Today!</p>
          <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto mb-8">
            <div className="bg-[#008080] text-white p-4 rounded-lg">
              <Phone className="w-8 h-8 mx-auto mb-2 text-[#DAA520]" />
              <p className="font-bold text-lg">+91 9766601155</p>
            </div>
            <div className="bg-[#008080] text-white p-4 rounded-lg">
              <Mail className="w-8 h-8 mx-auto mb-2 text-[#DAA520]" />
              <p className="font-bold text-lg">info@arkscaffolding.in</p>
            </div>
            <div className="bg-[#008080] text-white p-4 rounded-lg">
              <Globe className="w-8 h-8 mx-auto mb-2 text-[#DAA520]" />
              <p className="font-bold text-lg">www.arkscaffolding.in</p>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} ARK Scaffolding. All rights reserved.
          </p>
        </div>
      </div>

      {/* Enhanced Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          .page-break-before {
            page-break-before: always;
          }
          
          .page-break-after {
            page-break-after: always;
          }
          
          .page-break-inside-avoid {
            page-break-inside: avoid;
          }
          
          .brochure-content {
            max-width: 100%;
            padding: 15px;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          @page {
            margin: 1.5cm;
          }
        }
        
        @media screen {
          .brochure-content {
            background: #f8f9fa;
            padding: 40px;
          }
        }
      `}</style>
    </div>
  );
};

export default Brochure;
